// Section 22 header menu: Save, Load, Export, Import, Settings, How to play, Ledger, Title.
// A disclosure (not a modal): Esc or choosing an item closes it and returns focus to the button.
import { useRef, useState } from 'preact/hooks';
import { uiText } from '../text/en';

export interface MenuActions {
  onSave: () => void;
  onLoad: () => void;
  onExport: (shareable: boolean) => void;
  onImport: (file: File) => void;
  onSettings: () => void;
  onHowToPlay: () => void;
  onLedger: () => void;
  onTitle: () => void;
}

interface Props extends MenuActions {
  canSave: boolean;
}

export function Menu(props: Props) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const t = uiText.menu;
  const choose = (action: () => void) => () => {
    setOpen(false);
    button.current?.focus();
    action();
  };
  const items: [string, () => void, boolean][] = [
    [t.save, props.onSave, props.canSave],
    [t.load, props.onLoad, true],
    [t.exportSave, () => props.onExport(false), true],
    [t.exportRun, () => props.onExport(true), true],
    [t.import, () => file.current?.click(), true],
    [t.settings, props.onSettings, true],
    [t.howToPlay, props.onHowToPlay, true],
    [t.ledger, props.onLedger, true],
    [t.title, props.onTitle, true],
  ];
  return (
    <div
      class="menu"
      onFocusOut={(e) => {
        // Close when keyboard focus moves elsewhere; a null target (Safari clicks) keeps it open.
        const next = e.relatedTarget;
        if (open && next instanceof Node && !e.currentTarget.contains(next)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
          button.current?.focus();
        }
      }}
    >
      <button
        ref={button}
        type="button"
        class="button"
        aria-expanded={open}
        aria-controls="main-menu"
        onClick={() => setOpen(!open)}
      >
        {uiText.header.menu}
      </button>
      <ul id="main-menu" class="menu__list" hidden={!open}>
        {items.map(([label, action, enabled]) => (
          <li key={label}>
            <button type="button" class="menu__item" disabled={!enabled} onClick={choose(action)}>
              {label}
            </button>
          </li>
        ))}
      </ul>
      <input
        ref={file}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(e) => {
          const chosen = e.currentTarget.files?.[0];
          e.currentTarget.value = '';
          if (chosen !== undefined) props.onImport(chosen);
        }}
      />
    </div>
  );
}
